import * as _ from "lodash";
import { IFolderChangeEvent, IObjectTree, IObjectTreeDataManager, IObjectTreeFolder } from "../PA.Core.IF.ObjectTree";
import { BehaviorSubject } from "rxjs";
import { IMessageProvider } from "../PA.Core.IF.Basic";
import { WB_TreeFolderRow, WB_TreeRow } from "../PAdbSchemaRows";
import { MessageProvider } from "../Basic/Messages";
import { ExceptionMessage_restate, InfoMessage_restate } from "../Basic/MessageUtils";
import { PAdbContext } from "../PAdbContext";
import { esWB_Tree, esWB_Tree_Folder } from "../PAdbSchema";
import { ESRecordResolver } from "../lib/ESRecordResolver";
import { ObjectTreeFolder } from "./ObjectTreeFolder";

export interface ObjectTreeOptions {
    bSkipRecordResolution?: boolean;
    objectTreeContentProvider: IObjectTreeDataManager;
}

export class ObjectTree implements IObjectTree {
    private dbContext: PAdbContext;
    private treeRow: WB_TreeRow;
    private skipRecordResolution: boolean;
    private objectTreeContentProvider: IObjectTreeDataManager;
    private dctFolders: { [wbId: number]: ObjectTreeFolder };
    private loaded: boolean;
    private onLoadComplete: Promise<IObjectTree>;
    private loadMessageProvider: IMessageProvider;
    private onFoldersChanged: BehaviorSubject<IFolderChangeEvent>;

    private root: IObjectTreeFolder;

    constructor(treeRow: WB_TreeRow, options?: ObjectTreeOptions) {
        this.treeRow = treeRow;
        this.dbContext = <PAdbContext>treeRow.dataStore();

        this.skipRecordResolution = !!options?.bSkipRecordResolution;
        this.objectTreeContentProvider = options?.objectTreeContentProvider;
        this.dctFolders = {};

        this.loaded = false;
        this.onLoadComplete = undefined;
        this.loadMessageProvider = new MessageProvider();
        this.onFoldersChanged = new BehaviorSubject<IFolderChangeEvent>(undefined);
    }

    get bLoaded(): boolean {
        return this.loaded;
    }

    set bLoaded(value: boolean) {
        this.loaded = value;
    }

    get id(): string {
        return `Tree-${this.treeRow?.cif?.WB_TREE_ID}`;
    }

    get OnLoadComplete(): Promise<IObjectTree> {
        const self = this;
        if (self.loaded) return Promise.resolve(self);

        if (!_.isEmpty(self.onLoadComplete)) return self.onLoadComplete;

        let rv = (self.onLoadComplete = (async () => {
            try {
                self.root = {
                    wbId: 0,
                    Name: self.treeRow.cif.TREE_NAME,
                    Tree: self,
                    ContentDataManager: self.objectTreeContentProvider,
                    wbIdParent: 0,
                    Parent: undefined,
                    SubFolders: [],
                };

                let folderRows = self.treeRow.WB_TreeFolderRows;

                //-- Load using RR if not skipping
                if (!self.skipRecordResolution) {
                    let rr = new ESRecordResolver(self.dbContext, {
                        standardConditions: {
                            [esWB_Tree_Folder]: { DELETED_IND: { notIn: ["1"] } },
                        },
                    });

                    let treeRow: WB_TreeRow = <WB_TreeRow>(
                        _.first(await rr.findAllWithRelationships(esWB_Tree, [self.treeRow.cif.WB_TREE_ID]))
                    );
                    folderRows = treeRow.WB_TreeFolderRows;
                }

                // Add to dictionary
                for (let fldr of folderRows) {
                    if (!fldr.isActive) continue;

                    if (!(fldr.cif.WB_TREE_FOLDER_ID in self.dctFolders)) {
                        let f = new ObjectTreeFolder(self, fldr, undefined);
                        self.dctFolders[f.wbId] = f;
                    }
                }

                _.forEach(_.keys(self.dctFolders), (key) => {
                    if (!_.includes(_.map(folderRows, "WB_TREE_FOLDER_ID"), Number(key)))
                        delete self.dctFolders[Number(key)];
                });

                _.forEach(self.dctFolders, (tf, key) => {
                    let wbIdPrnt = tf.wbIdParent;
                    if (wbIdPrnt == 0) {
                        tf.setParent(self.root);
                        self.root.SubFolders.push(tf);
                        return;
                    }

                    if (wbIdPrnt in self.dctFolders) {
                        let prnt = self.dctFolders[wbIdPrnt];
                        tf.setParent(prnt);
                        prnt.addSubFolder(tf);
                        return;
                    }

                    // Orphaned Folder
                    tf.setParent(self.root);
                    self.root.SubFolders.push(tf);
                    InfoMessage_restate(
                        self.LoadMessageProvider,
                        true,
                        `Orphan-${tf.wbId}`,
                        `Orphan Folder, ${tf.Name} (${tf.wbId}), set to Root`,
                    );
                });

                self.loaded = true;
            } catch (e) {
                ExceptionMessage_restate(self.loadMessageProvider, e);
            }

            return self;
        })());

        return rv;
    }

    get LoadMessageProvider(): IMessageProvider {
        return this.loadMessageProvider;
    }

    get OnFoldersChanged(): BehaviorSubject<IFolderChangeEvent> {
        return this.onFoldersChanged;
    }

    get Root(): IObjectTreeFolder {
        return this.root;
    }

    async reload(): Promise<void> {
        this.skipRecordResolution = false;
        this.dctFolders = {};
        this.loaded = false;
        this.onLoadComplete = undefined;
        await this.OnLoadComplete;
    }

    async addFolder(sName: string, wbTreeFolderId_Prnt?: number): Promise<IObjectTreeFolder> {
        const self = this;

        //--
        let prnt: IObjectTreeFolder = undefined;
        let wbIdPrnt = wbTreeFolderId_Prnt || 0;
        if (wbIdPrnt == 0) prnt = self.root;
        else prnt = self.dctFolders[wbIdPrnt];

        if (_.isEmpty(prnt)) return;

        let fldr = <WB_TreeFolderRow>await self.dbContext.createRecordWithKey(esWB_Tree_Folder);
        fldr.cif.FOLDER_NAME = sName || "New Folder";
        fldr.cif.WB_TREE_ID = self.treeRow.cif.WB_TREE_ID;
        fldr.WB_TreeRow = self.treeRow;
        fldr.cif.WB_TREE_FOLDER_ID_PRNT = prnt.wbId;
        self.dbContext.add(esWB_Tree_Folder, fldr);
        let rv = new ObjectTreeFolder(self, fldr, prnt);
        prnt.SubFolders.push(rv);
        self.dctFolders[rv.wbId] = rv;

        self.OnFoldersChanged.next({ Tree: self, Change: "AddFolder", wbTreeFolderIds: [wbIdPrnt, rv.wbId] });

        return rv;
    }

    async removeFolder(wbTreeFolderId: number, bSkipNotify: boolean = false): Promise<void> {
        const self = this;

        if (!(wbTreeFolderId in self.dctFolders)) return;

        let f = self.dctFolders[wbTreeFolderId];
        //  Remove SubFolders
        for (let subfolder of f.SubFolders) {
            await self.removeFolder(subfolder.wbId, true);
        }

        // Find Contents, and Move to Root.
        if (self.objectTreeContentProvider) {
            let objs = await self.objectTreeContentProvider.getObjects(wbTreeFolderId);
            let dctObjsByMapper = _.groupBy(objs, (a) => a.mapperName);

            for (let mapperName in dctObjsByMapper) {
                let arr = dctObjsByMapper[mapperName];
                let arrWbIds = _.map(arr, (a) => a.wb_id);
                await self.objectTreeContentProvider.moveObjectsToFolder(mapperName, arrWbIds, 0);
            }
        }

        // Remove folder from Parent
        if (f.Parent) {
            _.remove(f.Parent.SubFolders, (a) => f.wbId == a.wbId);
        }

        f.Delete();

        if (!bSkipNotify) {
            self.OnFoldersChanged.next({ Tree: self, Change: "RemoveFolder", wbTreeFolderIds: [wbTreeFolderId, 0] });
        }
        return;
    }

    async moveFolderTo(wbTreeFolderId_Src?: number, wbTreeFolderId_Tgt?: number): Promise<void> {
        const self = this;

        if (!(wbTreeFolderId_Src in self.dctFolders)) return;

        let f = self.dctFolders[wbTreeFolderId_Src];

        const currParent = f.Parent;
        if (!currParent) return;

        if (currParent.wbId == wbTreeFolderId_Tgt) return;

        // Check if target Exists
        if (wbTreeFolderId_Tgt == 0) {
            // Move to Root
            _.remove(currParent.SubFolders, (a) => a.wbId == f.wbId);
            f.setParent(self.root);
            self.root.SubFolders.push(f);
        } else {
            if (wbTreeFolderId_Tgt in self.dctFolders) {
                const tgt = self.dctFolders[wbTreeFolderId_Tgt];
                _.remove(currParent.SubFolders, (a) => a.wbId == f.wbId);
                f.setParent(tgt);
                tgt.addSubFolder(f);
            }
        }

        self.OnFoldersChanged.next({
            Tree: self,
            Change: "MoveFolderTo",
            wbTreeFolderIds: [wbTreeFolderId_Src, wbTreeFolderId_Tgt],
        });
    }
}
